<template>
  <div id="mental-health-survey">
    <div class="col-12 survey-header">
      <router-link to="/dashboard">
        <img
          class="header-image"
          src="@/assets/img/bettermynd-logo@2x.png"
          alt="BetterMynd"
        >
      </router-link>
    </div>

    <card v-if="!started" class="col-12 sm-survey-content">
      
      <welcome
        @start="started = true"
        @skip="skipToDashboard"
      />

    </card>

    <card v-else-if="!showResults" class="col-12 sm-survey-content">

      <survey-template-workflow
        :survey="survey"
        v-model="responses"
        @submit="showResults = true"
        @responded="responded"
      />

    </card>

    <card v-else class="col-12 lg-survey-content">
      <review 
        :survey="survey"
        :responses="responses"
        :slugs="review_slugs"
        @reset="showResults = false"
        @submit="complete_survey"
      />
    </card>
    <get-help-modal ref="getHelpModal" />
  </div>
</template>

<script>
import Card from '@/components/Card.vue';
import GetHelpModal from '@/crisis/GetHelpModal.vue';
import Loader from '@/components/Loader';
import SurveyClient from '@/api/SurveyClient'
import SurveyTemplateWorkflow from '@/surveys/survey-template-workflow/SurveyTemplateWorkflow.vue';
import Review from './components/Review.vue';
import Welcome from './components/Welcome.vue';

export default {
  name: 'MentalHealthSurvey',
  components: {
    Card,
    Loader,
    GetHelpModal,
    SurveyTemplateWorkflow,
    SurveyClient,
    Review,
    Welcome,
  },
  data() {
    return {
      surveyClient: new SurveyClient(this.$http),
      survey_response_id: 0,
      survey: null, // template

      responses: {},
      started: false,
      showResults: false,

      review_slugs: [
        "when-was-the-last-time-you-att",
        "were-you-given-a-diagnosis",
        "are-you-currently-taking-any-p",
        "what-are-you-struggling-with",
        "do-you-have-a-gender-preferenc",
        "mental-health-survey-counselor-religion",
        "mental-health-survey-counselor-language",
        "do-you-have-a-preference-for-y",
        "would-you-prefer-a-provider-wh",
      ],
    };
  },
  async mounted() {
    if (this.$store.getters.currentUser && this.$store.getters.currentUser.needsWelcome) {
      return this.$router.push({ name: this.$store.getters.currentUser.homeRouteName });
    }

    try {
      const userId = this.$store.getters.currentUser.id
      let response = await this.surveyClient.getLatestResponseFromSlug(userId, 'mental-health-survey')
      if (!response)
        response = await this.surveyClient.createResponse(userId, 'mental-health-survey')

      this.responses = response.responses
      this.survey = response.survey
      this.survey_response_id = response.external_id
    } catch(ex) {
      console.error(ex)
      this.skipToDashboard()
    }
  },
  methods: {
    skipToDashboard() {
      // store skip
      const user = this.$store.getters.currentUser;
      user.skippedSurvey = true;
      this.$store.commit('setUser', user);

      // go
      this.$router.push({ name: this.$store.getters.currentUser.homeRouteName });
    },

    async responded({question, answer, next, interupt}){
      if(!this.isInCrisis(question, answer) && await this.saveQuestionResponse(question, answer))
        next(); 
      else
        interupt();
    },
    isInCrisis(question, answer) {
      const isInCrisis = question.slug === 'do-you-feel-like-youre-a-threa'
        && (answer.selections.includes('yes') || answer.selections.includes('im-not-sure'))
      
      if (isInCrisis) {
        this.$refs.getHelpModal.show();
      }

      return isInCrisis
    },
    async saveQuestionResponse(question, answer) {
      try 
      {
        const slug = question.slug

        if(question.multiple_choice)
          await this.surveyClient.respondToMultipleChoiceQuestion(this.survey_response_id, slug, answer)
        else if (question.free_text)
          await this.surveyClient.respondToFreeTextQuestion(this.survey_response_id, slug, answer)
        else if (question.star_rating)
          await this.surveyClient.respondToStarRatingQuestion(this.survey_response_id, slug, answer)
        else
          throw Error("Unknown question type")
          
        return true;
      } 
      catch (ex)
      {
        this.emit_error(ex, 'Failed to save answer! Please try again.')
        return false;
      }
    },
    async complete_survey() {
      try {
        await this.surveyClient.submitSurvey(this.survey_response_id)

        // store that its done
        const user = await this.$globals.userClient.getRemoteUser();
        user.student_profile.completed_survey = true;
        this.$store.commit('setUser', user);

        this.$router.push({ name: user.homeRouteName })
      } catch (ex) {
        this.emit_error(ex, 'Failed to submit survey! Please try again.')
      }
    },
    emit_error(ex, message) {
      console.error(ex)
      let errorMessage = message;
      if (ex && ex.body && ex.body.error && ex.body.error.extra && ex.body.error.extra.detail) {
        errorMessage = ex.body.error.extra.detail;
      } else if (ex && ex.body && ex.body.error && ex.body.error.message) {
        errorMessage = ex.body.error.message;
      }

      this.$store.commit('setGlobalAlert', {
        msg: errorMessage, error: true,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
#mental-health-survey {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;

  .survey-header {
    margin: 10px;
    width: 100%;

    .header-image {
      height: 48px;
      margin-bottom: 0;
    }
  }

  .sm-survey-content {
    max-width: 660px;
  }

  .lg-survey-content {
    max-width: 1000px;
  }
}
</style>
